import React from "react";
import { Button, IconButton, MenuItem, TextField, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import styles from "../../containers/vresume/VResume.module.css";

/**
 * A schema-driven editor for a list of small records — formation, langues, certifications.
 *
 * The three sections are the same interaction (add a row, fill a few short fields, remove a
 * row) over different fields, so they share one component described by a `fields` array
 * instead of being written three times. `VResumeEditor` passes the schema; nothing about
 * education, languages or certifications is known here.
 *
 * ── Field schema ──────────────────────────────────────────────────────────────────────
 *
 *   { key, label, width?, type?, options?, placeholder? }
 *
 * `width` is a flex-basis in px, since these rows are mixes of narrow (a year) and wide (a
 * diploma) inputs and a uniform grid would waste most of the row on the years.
 * `type: 'select'` uses `options: [{value, label}]`.
 *
 * Purely presentational: `onChange` receives the complete new array, matching what the
 * autosave sends and what the backend's sanitizers expect.
 */
export default function RepeatingRows({
    rows = [],
    onChange,
    fields = [],
    disabled = false,
    addLabel,
    emptyLabel,
    removeTooltip,
    /** Matches `MAX_LIST_ITEMS` in the backend's helpers/vresume.js. */
    maxRows = 40
}) {

    const setRow = (index, key, value) => {
        const next = (rows || []).map((row, i) => i === index ? { ...row, [key]: value } : row)
        onChange(next)
    }

    const addRow = () => {
        // Seeded with every key present and empty rather than `{}`, so a controlled
        // TextField never receives `undefined` — React logs a warning and the input flips to
        // uncontrolled for the rest of its life if it does.
        const blank = fields.reduce((acc, field) => ({ ...acc, [field.key]: '' }), {})
        onChange([...(rows || []), blank])
    }

    const removeRow = (index) => {
        onChange((rows || []).filter((row, i) => i !== index))
    }

    return (
        <div>
            {(rows || []).length === 0
                ? <div className={styles.emptyRows}>{emptyLabel}</div>
                : (
                    <div className={styles.rowList}>
                        {rows.map((row, index) => (
                            <div className={styles.row} key={index}>
                                <div className={styles.rowFields}>
                                    {fields.map(field => (
                                        <TextField
                                            key={field.key}
                                            size="small"
                                            disabled={disabled}
                                            select={field.type === 'select'}
                                            label={field.label}
                                            placeholder={field.placeholder}
                                            // `?? ''` and not `|| ''`: a legitimate 0 in a
                                            // field would otherwise be replaced by a blank.
                                            value={row[field.key] ?? ''}
                                            onChange={(event) => setRow(index, field.key, event.target.value)}
                                            sx={{
                                                flex: field.width ? `0 1 ${field.width}px` : '1 1 160px',
                                                minWidth: field.width ? 0 : 140
                                            }}
                                            InputProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                                            InputLabelProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                                        >
                                            {field.type === 'select'
                                                ? [
                                                    // An explicit empty choice: a level is
                                                    // optional on a language, and without
                                                    // this a value set by mistake could not
                                                    // be cleared.
                                                    <MenuItem value="" key="__empty__">
                                                        <span style={{ opacity: 0.5 }}>—</span>
                                                    </MenuItem>,
                                                    ...(field.options || []).map(option => (
                                                        <MenuItem value={option.value} key={option.value}
                                                            sx={{ fontFamily: 'roboto', fontSize: 13 }}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))
                                                ]
                                                : null}
                                        </TextField>
                                    ))}
                                </div>
                                {!disabled
                                    ? <Tooltip title={removeTooltip || ''} arrow>
                                        <IconButton size="small" onClick={() => removeRow(index)}
                                            sx={{ mt: 0.5 }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 18, color: 'var(--red)' }} />
                                        </IconButton>
                                    </Tooltip>
                                    : null}
                            </div>
                        ))}
                    </div>
                )}

            {!disabled && (rows || []).length < maxRows
                ? <Button size="small" startIcon={<AddIcon />} onClick={addRow}
                    sx={{
                        mt: 1, textTransform: 'none', fontFamily: 'roboto', fontSize: 12.5,
                        color: 'var(--orange)'
                    }}>
                    {addLabel}
                </Button>
                : null}
        </div>
    )
}
